import {
  DestroyRef,
  Injectable,
  Injector,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { CoreStore, UserStore } from '@qtek/libs/store';
import { TranslationCoreService } from '@qtek/libs/translation-core';
import { ROLE_LABELS, UserRole } from '@qtek/shared/models';
import { clearSsoWelcome, hasSsoWelcome } from '@qtek/shared/utils';
import { combineLatest, filter, fromEvent, take } from 'rxjs';

export interface SsoWelcome {
  company: string;
  roleLabel: string;
}

const pathOf = (url: string): string => url.split(/[?#]/)[0];

/**
 * Says welcome to someone who has just created an account by signing in with Microsoft.
 *
 * The account is made at the lowest role the organization configured, which may leave the person
 * with nothing to open, so the first page says what happened to them ("you were added to X as Y")
 * instead of leaving an empty screen unexplained.
 *
 * The login app leaves a marker in session storage. The welcome is held until the application is
 * fully loaded and routing has settled, so the initial redirects (`'' -> dash`, guards) are done and
 * it appears on the page the person actually lands on. It is shown on that page only and comes
 * back whenever they return to it; it ends for good when they close it or the window is reloaded.
 *
 * Provided by the layout component, so it lives and dies with the layout.
 */
@Injectable()
export class SsoWelcomeService {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private userStore = inject(UserStore);
  private coreStore = inject(CoreStore);
  private translate = inject(TranslationCoreService);

  private readonly state = signal<SsoWelcome | null>(null);
  private readonly landing = signal<string | null>(null);
  private readonly currentPath = signal(pathOf(this.router.url));

  /**
   * The welcome to display, or `null` when there is nothing to show. It is only shown on the page
   * the person landed on: elsewhere it is hidden, not dismissed, and returns with them.
   */
  readonly welcome = computed(() =>
    this.currentPath() === this.landing() ? this.state() : null
  );

  init(): void {
    if (!hasSsoWelcome()) {
      return;
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event =>
        this.currentPath.set(pathOf(event.urlAfterRedirects))
      );

    // A reload or tab close ends the welcome. Reading the marker does not clear it, so without
    // this a refresh on the landing page would show it again.
    fromEvent(window, 'pagehide')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => clearSsoWelcome());

    combineLatest([
      toObservable(this.userStore.mainCompany, { injector: this.injector }),
      toObservable(this.userStore.relationType, { injector: this.injector }),
      toObservable(this.coreStore.displayLoading, { injector: this.injector }),
      toObservable(this.router.currentNavigation, { injector: this.injector }),
    ])
      .pipe(
        filter(
          ([company, role, loading, navigation]) =>
            !!company?.companyName && !!role && !loading && !navigation
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([company, role]) => {
        const roleKey = ROLE_LABELS[role as UserRole];
        this.state.set({
          company: company?.companyName ?? '',
          roleLabel: roleKey ? this.translate.instant(roleKey) : String(role),
        });
        this.landing.set(pathOf(this.router.url));
      });
  }

  dismiss(): void {
    this.state.set(null);
    clearSsoWelcome();
  }
}
